import { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommentQueueItem = ({ item, onEdit, onDelete, onReschedule }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-primary/10 text-primary',
      'Processing': 'bg-warning/10 text-warning',
      'Posted': 'bg-success/10 text-success',
      'Failed': 'bg-error/10 text-error',
      'Paused': 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatScheduledTime = (date) => {
    const now = new Date();
    const scheduled = new Date(date);
    const diffMs = scheduled - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    if (diffHours > 0) return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    if (diffMins > 0) return `in ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
    return 'soon';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-card transition-all duration-150">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <Image
            src={item?.targetUser?.avatar}
            alt={item?.targetUser?.avatarAlt}
            className="w-12 h-12 rounded-full object-cover"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground truncate">{item?.targetUser?.name}</h3>
              <p className="text-xs text-muted-foreground">@{item?.targetUser?.username}</p>
            </div>
            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(item?.status)}`}>
              {item?.status}
            </span>
          </div>

          <div className="bg-muted rounded-lg p-3 mb-3">
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {isExpanded ? item?.comment : `${item?.comment?.substring(0, 100)}${item?.comment?.length > 100 ? '...' : ''}`}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-xs text-foreground">{formatScheduledTime(item?.scheduledTime)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="FileText" size={14} className="text-muted-foreground" />
              <span className="text-xs text-foreground truncate">{item?.templateName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Target" size={14} className="text-muted-foreground" />
              <span className="text-xs text-foreground">{item?.targetPost}</span>
            </div>
          </div>

          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Platform</p>
                  <div className="flex items-center gap-2">
                    <Icon name={item?.platform === 'Instagram' ? 'Instagram' : item?.platform === 'Facebook' ? 'Facebook' : 'Twitter'} size={16} className="text-foreground" />
                    <span className="text-sm font-medium text-foreground">{item?.platform}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Campaign</p>
                  <p className="text-sm font-medium text-foreground">{item?.campaign || 'None'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Retry Count</p>
                  <p className="text-sm font-medium text-foreground">{item?.retryCount || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Priority</p>
                  <p className="text-sm font-medium text-foreground">{item?.priority}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => onEdit(item)}
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => onReschedule(item)}
            >
              Reschedule
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              onClick={() => onDelete(item)}
            />
            <Button
              variant="ghost"
              size="sm"
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Less' : 'More'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentQueueItem;